export interface HNoteFilterOption {
  key: string;
  label: string;
  selected?: boolean;
  parentKey?: string;
}

export interface HierarchyLookupNode {
  id?: string | null;
  parentId?: string | null;
  code?: string | null;
  name?: string | null;
  state?: string | null;
  childLookup?: HierarchyLookupNode[] | null;
}

function isActiveHierarchyLookup(state: unknown): boolean {
  if (state == null) {
    return true;
  }
  return String(state).toUpperCase() === 'ACTIVE';
}

export function collectHierarchyFilterOptions(roots: HierarchyLookupNode[]): HNoteFilterOption[] {
  const result: HNoteFilterOption[] = [];

  const walk = (node: HierarchyLookupNode, depth: number): void => {
    if (depth > 0 && node.id) {
      if (isActiveHierarchyLookup(node.state)) {
        const label = (node.name ?? node.code ?? '').trim();
        result.push({
          key: node.id,
          label: label || node.id,
          parentKey: node.parentId ?? undefined,
        });
      }
    }
    for (const child of node.childLookup ?? []) {
      if (child) {
        walk(child, depth + 1);
      }
    }
  };

  for (const root of roots) {
    if (root) {
      walk(root, 0);
    }
  }

  return result;
}

export interface HNoteAttachment {
  blobName: string;
  fileName: string;
  createdOn: Date;
  attachmentURL?: string;
}

export interface HNoteItem {
  noteKey: string;
  noteId?: string;
  noteTypedBy: string;
  positionFamily: string;
  hierarchyPath?: string;
  /** Ordered labels from `noteTypeParents` (root → leaf) for breadcrumb UI. */
  noteTypeHierarchy?: string[];
  noteText: string;
  attachments: HNoteAttachment[];
  createdOn: Date;
  updatedOn?: Date;
  showHistory?: boolean;
  olderNotes?: HNoteItem[];
  isUserUnAuthorizedToEdit?: boolean;
  /** Leaf note-type id used to group version history. */
  noteTypeKey?: string;
  typeKey?: string;
  categoryKey?: string;
  subCategoryKey?: string;
  userKey?: string;
  userPic?: string;
}

export interface MongoNoteAttachment {
  attachmentId: string;
  attachmentUrl: string;
}
export interface MongoNoteWithTicketStatus {
  id: string;
  objectID: string;      // status change id
  businessId: string;
  noteText: string;
  noteModuleCode: string;
  noteTypeId: string | null;
  createdOn: string;
  createdById: string | null;
  updatedOn: string;
  updatedById: string | null;
  noteType: string | null;      // from ticket status lookup
  noteTypeCode: string | null;
  userName: string | null;
  userPic: string | null;
  attachments: MongoNoteAttachment[];
}

/** Maps ticket status notes API shape to {@link MongoNoteDetail} for shared list UI. */
export function mapMongoNoteWithTicketStatusToDetail(
  note: MongoNoteWithTicketStatus
): MongoNoteDetail {
  return {
    noteId: note.id,
    businessId: note.businessId,
    objectId: note.objectID,
    noteModuleCode: note.noteModuleCode,
    noteText: note.noteText,
    noteTypeId: note.noteTypeId,
    attachments: note.attachments ?? [],
    attachmentCount: note.attachments?.length ?? 0,
    createdOn: note.createdOn,
    createdById: note.createdById,
    updatedOn: note.updatedOn,
    updatedById: note.updatedById,
    userName: note.userName,
    userPic: note.userPic,
    createdByUserName: note.userName,
    createdByUserPic: note.userPic,
    noteTypeParents: [],
  };
}

export interface HNotesPaginated {
  data: HNoteItem[];
  total: number;
  pageSize: number;
}

export interface HNotesRequest {
  page: number;
  pageSize: number;
  sortColumn: number;
  sortDirection: boolean;
  typeKeys: string;
  categoryKeys: string;
  subCategoryKeys: string;
  userKeys: string;
  search: string;
  startDate: string;
  endDate: string;
}

// Shape returned by GET /MongoNotes/getNotes/{objectId}
export type BusinessLookupHierarchyItem = {
  id: string;
  parentId: string | null;
  code: string | null;
  name: string | null;
  lookupModuleCode: string | null;
};

export type MongoNoteDetail = {
  noteId: string;
  businessId: string;
  objectId: string;
  noteModuleCode: string;
  noteText: string;
  noteTypeId: string | null;
  attachments: ApiNoteAttachment[];
  attachmentCount: number;
  createdOn: string;
  createdById: string | null;
  updatedOn: string;
  updatedById: string | null;
  userName: string | null;
  userPic: string | null;
  createdByUserName: string | null;
  createdByUserPic: string | null;
  noteTypeParents: BusinessLookupHierarchyItem[];
};

export interface ApiNoteAttachment {
  attachmentId: string;
  attachmentUrl: string;
}

function orderNoteTypeParents(
  parents: BusinessLookupHierarchyItem[]
): BusinessLookupHierarchyItem[] {
  if (!parents.length) return [];
  // Root-first when the first item has no parent (typical API shape).
  if (parents[0]?.parentId == null) return parents;
  // Leaf-first: reverse to root → leaf.
  return [...parents].reverse();
}

export function mapApiNoteToHNoteItem(api: MongoNoteDetail): HNoteItem {
  const noteTypeParents = Array.isArray(api.noteTypeParents) ? api.noteTypeParents : [];
  const orderedParents = orderNoteTypeParents(noteTypeParents);
  const hierarchyLabels = orderedParents
    .map((p) => (p?.name ?? '').trim())
    .filter(Boolean);
  const hierarchyPath = hierarchyLabels.length ? hierarchyLabels.join(',') : undefined;
  const typeKey = orderedParents[0]?.id ?? api.noteTypeId ?? undefined;
  const categoryKey = orderedParents[1]?.id ?? undefined;
  const subCategoryKey = orderedParents[2]?.id ?? undefined;
  const leafTypeId =
    api.noteTypeId ?? orderedParents[orderedParents.length - 1]?.id ?? undefined;

  return {
    noteKey: api.noteId,
    noteId: api.noteId,
    noteTypedBy: api.userName ?? api.createdByUserName ?? '',
    positionFamily: '',
    hierarchyPath,
    noteTypeHierarchy: hierarchyLabels.length ? hierarchyLabels : undefined,
    noteText: api.noteText ?? '',
    attachments: (api.attachments ?? []).map((a) => ({
      blobName: a.attachmentUrl ?? '',
      fileName: extractFileNameFromUrl(a.attachmentUrl),
      createdOn: new Date(api.createdOn),
      attachmentURL: a.attachmentUrl,
    })),
    createdOn: new Date(api.createdOn),
    updatedOn: api.updatedOn ? new Date(api.updatedOn) : undefined,
    noteTypeKey: leafTypeId,
    typeKey,
    categoryKey,
    subCategoryKey,
    userKey: api.createdById ?? undefined,
    userPic: api.userPic ?? api.createdByUserPic ?? undefined,
  };
}

function getNoteTypeGroupKey(note: HNoteItem): string {
  return (
    note.noteTypeKey ??
    note.subCategoryKey ??
    note.categoryKey ??
    note.typeKey ??
    note.hierarchyPath ??
    note.noteKey
  );
}

/** Latest note per type on the row; older versions of the same type in `olderNotes`. */
export function groupNotesByNoteType(notes: HNoteItem[]): HNoteItem[] {
  const byType = new Map<string, HNoteItem[]>();
  for (const note of notes) {
    const key = getNoteTypeGroupKey(note);
    const group = byType.get(key) ?? [];
    group.push(note);
    byType.set(key, group);
  }

  const grouped: HNoteItem[] = [];
  for (const group of byType.values()) {
    const sorted = [...group].sort(
      (a, b) =>
        (b.updatedOn ?? b.createdOn).getTime() - (a.updatedOn ?? a.createdOn).getTime()
    );
    const [latest, ...older] = sorted;
    grouped.push({
      ...latest,
      olderNotes: older.length
        ? older.map((n) => ({ ...n, olderNotes: undefined, showHistory: false }))
        : undefined,
      showHistory: false,
    });
  }
  return grouped;
}

function extractFileNameFromUrl(url: string | undefined): string {
  if (!url) return '';
  try {
    const path = new URL(url).pathname;
    return decodeURIComponent(path.split('/').pop() ?? '');
  } catch {
    return url.split('/').pop() ?? '';
  }
}

export const STATIC_HR_NOTE_FILTERS = {
  rootNoteTypes: [
    { key: '1', label: 'HR General' },
    { key: '2', label: 'Employee Relations' },
    { key: '3', label: 'Payroll' },
  ] as HNoteFilterOption[],
  categoryNoteTypes: [
    { key: '10', label: 'Onboarding', parentKey: '1' },
    { key: '11', label: 'Policy', parentKey: '1' },
    { key: '20', label: 'Disciplinary', parentKey: '2' },
    { key: '30', label: 'Salary Review', parentKey: '3' },
  ] as (HNoteFilterOption & { parentKey?: string })[],
  subCategoryNoteTypes: [
    { key: '100', label: 'Welcome Pack', parentKey: '10' },
    { key: '101', label: 'Handbook', parentKey: '11' },
    { key: '200', label: 'Warning Letter', parentKey: '20' },
  ] as (HNoteFilterOption & { parentKey?: string })[],
};
